import {
  Badge,
  Button,
  Card,
  CardBody,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { ClosingEditDateModal } from "features/closings/ClosingEditDateModal";
import { ClosingPageMenu } from "features/closings/ClosingMenu";
import { ClosingTabProps } from "features/closings/tabs/ClosingTabProps";
import {
  getClosingListQueryKey,
  getClosingRetrieveQueryKey,
  useClosingOrderOfOperationsRetrieveSuspense,
  useClosingRetrieveSuspense,
  useClosingStatsRetrieveSuspense,
  useClosingToApprovalUpdate,
  useClosingToCompleteUpdate,
} from "gen/endpoints/closing/closing";
import { getClosingTableRetrieveQueryKey } from "gen/endpoints/closing-table-data/closing-table-data";
import { useCommitmentInformationTableDataRetrieveSuspense } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { ClosingOrderOfOperationsEnum } from "gen/model/closingOrderOfOperationsEnum";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { GenericTableBadgeTypeEnum } from "gen/model/genericTableBadgeTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { isEmpty } from "lodash-es";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageTabs, PageTabsStatus } from "shared/components/layout/PageTabs";
import { ActionConfirmationDialog } from "shared/components/modal/ActionConfirmationDialog";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { Edit2Icon } from "shared/icons/Edit2Icon";
import { rootRoute } from "shared/navigation/Routes";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { useHasPermission } from "shared/utils/PermissionUtils";

const getStateColor = (
  state: FundFamilyEventStateMachineFieldsEnum,
): GenericTableBadgeTypeEnum => {
  switch (state) {
    case FundFamilyEventStateMachineFieldsEnum.draft:
      return "warning";
    case FundFamilyEventStateMachineFieldsEnum.completed:
      return "success";
    case FundFamilyEventStateMachineFieldsEnum.out_of_date:
      return "error";
    default:
      return "default";
  }
};

export const ClosingEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["closing", "common", "enums"]);
  const { fundFamilyId, closingId } = useRequiredStringParams();
  const queryClient = useQueryClient();

  const { data: closingData } = useClosingRetrieveSuspense(
    fundFamilyId,
    closingId,
  );
  const { data: closingOrderOfOperationsData } =
    useClosingOrderOfOperationsRetrieveSuspense(fundFamilyId, closingId);

  const {
    mutateAsync: sendClosingForApproval,
    isPending: isSendClosingForApprovalLoading,
  } = useClosingToApprovalUpdate();
  const { mutateAsync: completeClosing, isPending: isCompleteClosingLoading } =
    useClosingToCompleteUpdate();
  const isLoading = isSendClosingForApprovalLoading || isCompleteClosingLoading;

  const commitmentsClosingPagePath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .commitments({})
    .closingsTab({}).$;

  const confirmFinalize = useConfirmationDialog(ActionConfirmationDialog, {
    title: t("closing:finalize"),
    subtitle: t("closing:finalizeModal.subtitle"),
    description: t("closing:finalizeModal.description"),
    successMessage: t("closing:finalizeModal.successMessage"),
    performAction: async () => {
      await sendClosingForApproval({ fundFamilyId, closingId });
      await completeClosing({ fundFamilyId, closingId });
      await Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: getClosingTableRetrieveQueryKey(fundFamilyId),
        }),
        queryClient.invalidateQueries({
          queryKey: getClosingListQueryKey(fundFamilyId, {
            is_draft: true,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: getClosingRetrieveQueryKey(fundFamilyId, closingId),
        }),
      ]);
      navigate(commitmentsClosingPagePath);
    },
  });

  const {
    isOpen: isEditDateModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  const { data: commitments } =
    useCommitmentInformationTableDataRetrieveSuspense({
      fund_family_id: fundFamilyId,
      closing_id: closingId,
    });

  const { data: statData } = useClosingStatsRetrieveSuspense(
    fundFamilyId,
    closingId,
  );

  const fundFamilyEventId = closingData.fund_family_event_id;
  const { data: fundFamilyEventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const isDraft =
    fundFamilyEventData.state === FundFamilyEventStateMachineFieldsEnum.draft;

  const doesClosingHaveNoCommitments = isEmpty(commitments.rows);

  const shouldRunEqualizationFirst =
    closingOrderOfOperationsData.closing_order_of_operations ===
    ClosingOrderOfOperationsEnum.equalization_first;

  const disabledTabs: PageTabsStatus = {
    closingCommitments: false,
    equalizationFirstClosingEqualization: doesClosingHaveNoCommitments,
    equalizationSecondClosingEqualization: doesClosingHaveNoCommitments,
    equalizationFirstClosingFeeTrueUp: doesClosingHaveNoCommitments,
    equalizationSecondClosingFeeTrueUp: doesClosingHaveNoCommitments,
  };
  const hiddenTabs: PageTabsStatus = {
    closingCommitments: false,
    equalizationFirstClosingEqualization:
      !isDraft || !shouldRunEqualizationFirst,
    equalizationSecondClosingEqualization:
      !isDraft || shouldRunEqualizationFirst,
    equalizationFirstClosingFeeTrueUp: !shouldRunEqualizationFirst,
    equalizationSecondClosingFeeTrueUp: shouldRunEqualizationFirst,
  };
  const tabProps: ClosingTabProps = {
    fundFamilyId,
    closingId,
  };

  const hasWritePermission = useHasPermission(PermissionTypeEnum.write);

  // NOTE: we will eventually shift this to be a refresh rather than discard of the closing
  const discardClosing = async () => {
    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: getClosingTableRetrieveQueryKey(fundFamilyId),
      }),
      queryClient.invalidateQueries({
        queryKey: getClosingListQueryKey(fundFamilyId, {
          is_draft: true,
        }),
      }),
    ]);
    navigate(commitmentsClosingPagePath);
  };

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("common:closing")}
        backButton={{
          title: t("common:closings"),
          path: commitmentsClosingPagePath,
        }}
        badge={
          <HStack spacing="8px">
            <Badge colorScheme={getStateColor(fundFamilyEventData.state!)}>
              {t(
                `enums:FundFamilyEventStateMachineFieldsEnum.${fundFamilyEventData.state!}`,
              )}
            </Badge>
            <Badge
              as="div"
              display="flex"
              alignItems="center"
              variant={isDraft && hasWritePermission ? "button" : undefined}
              onClick={() => {
                if (isDraft && hasWritePermission) {
                  openEditDateModal();
                }
              }}
            >
              <Text>{formatIsoDateToDisplay(closingData.closing_date)}</Text>
              {isDraft && hasWritePermission && <Edit2Icon />}
            </Badge>
          </HStack>
        }
        actions={
          <>
            {isDraft && (
              <Restricted permission={PermissionTypeEnum.approve}>
                <Button
                  colorScheme="secondary"
                  size="sm"
                  isDisabled={isLoading}
                  onClick={confirmFinalize}
                >
                  {t("closing:finalize")}
                </Button>
              </Restricted>
            )}
            <ClosingPageMenu
              fundFamilyId={fundFamilyId}
              closingData={{
                closing_id: closingId,
                closing_date: closingData.closing_date,
                state: fundFamilyEventData.state,
                fund_family_event_id: fundFamilyEventId,
              }}
              onDiscard={() => {
                navigate(commitmentsClosingPagePath);
              }}
            />
          </>
        }
      />
      <PageBody>
        {!!fundFamilyEventData.out_of_date_reason && (
          <OutOfDateAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            message={fundFamilyEventData.out_of_date_reason}
            onDiscard={discardClosing}
          />
        )}
        <Card variant="stat" marginBottom={8} data-ls-enabled={false}>
          <CardBody>
            <CardSectionGroup>
              <CardSection>
                <CardSectionBody>
                  <StatGroup>
                    <Stat size="sm">
                      <StatNumber>
                        {`${formatMoney(
                          statData.total_amount_raised_to_date,
                        )} (+${formatMoney(
                          statData.total_amount_raised_in_closing,
                        )})`}
                      </StatNumber>
                      <StatLabel>
                        {t("closing:stats.totalAmountRaised")}
                      </StatLabel>
                    </Stat>
                    <Stat size="sm">
                      <StatNumber>
                        {`${statData.total_investors_to_date} (+${statData.total_new_investors_in_closing} ${t(
                          "common:new",
                        )})`}
                      </StatNumber>
                      <StatLabel>{t("closing:stats.totalInvestors")}</StatLabel>
                    </Stat>
                    <Stat size="sm">
                      <StatNumber>
                        {formatMoney(
                          statData.total_fee_true_up_amount_in_closing,
                        )}
                      </StatNumber>
                      <StatLabel>{t("closing:stats.feeTrueUps")}</StatLabel>
                    </Stat>
                  </StatGroup>
                </CardSectionBody>
              </CardSection>
            </CardSectionGroup>
          </CardBody>
        </Card>
        <PageTabs
          context={tabProps}
          disabledTabs={disabledTabs}
          hiddenTabs={hiddenTabs}
        />
      </PageBody>
      {isEditDateModalOpen && (
        <ClosingEditDateModal
          fundFamilyId={fundFamilyId}
          closingId={closingId}
          onCloseComplete={closeEditDateModal}
        />
      )}
    </PageContent>
  );
};
